/**
 * What the case IDE's viewers and editors read and save through, so the same
 * PDF, Word, Excel and PowerPoint editors open a case file (`caseFileStore`)
 * or a client document (`clientDocumentStore`). Every save is a new version
 * of the file; a save based on bytes that changed since they were loaded
 * fails with an `APIError` 409.
 */
import { downloadFile } from "../../api/OpenAIApi";
import { fileRecoveryKey } from "../draftEditor/docxRecovery";
import { FilesystemAPI } from "./api/filesystemApi";
import { nodeFilename, nodeTitle } from "./nodeNames";
import type { FsNode, FsNodeVersion } from "./types";

export type EditableFileVersion = Pick<
  FsNodeVersion,
  "created_at" | "note" | "page_count"
>;

export type EditableFile = {
  /**
   * Unique across stores (`file:<node id>`, `client-document:<id>`): unsaved
   * edit checkpoints and the unsaved-documents guard key on it.
   */
  key: string;
  id: string;
  /** What a person reads. */
  title: string;
  /** `title.ext`: picks the viewer and names downloads. */
  filename: string;
  mimeType: string | null;
  checksum: string | null;
  /** Changes whenever the stored bytes do, so viewers know to reload. */
  revision: string | null;
  /** Earlier versions, oldest first. */
  versions: EditableFileVersion[];
  hasBytes: boolean;
};

/**
 * Set only when the file is a case file open in the Case IDE: the editors then
 * register with the live agent and offer Make This a Draft.
 */
export type FileCaseContext = {
  individualId: string;
  /** Open the new draft in the IDE after Make This a Draft. */
  onDraftCreated?: (draftId: string, title: string) => void;
};

export type FileSaveOptions = {
  note?: string;
  /** The checksum the editor loaded; null saves over whatever is there. */
  baseChecksum?: string | null;
};

export type EditableFileStore = {
  file: EditableFile;
  /** The stored bytes as uploaded. */
  loadOriginal: () => Promise<Blob>;
  /** The file as a PDF (converted by the server when it isn't one). */
  loadPdf: () => Promise<Blob>;
  /** Save Word, Excel or PowerPoint bytes as a new version. */
  replaceBytes: (blob: Blob, options: FileSaveOptions) => Promise<EditableFile>;
  /** Save the PDF editor's output as a new version (an image becomes a PDF). */
  replacePdf: (
    pdf: Blob,
    options: FileSaveOptions & { rotations?: Record<number, number> }
  ) => Promise<EditableFile>;
  restoreVersion: (index: number) => Promise<EditableFile>;
};

export const caseFileOf = (node: FsNode): EditableFile => ({
  key: fileRecoveryKey(node.id),
  id: node.id,
  title: nodeTitle(node),
  filename: nodeFilename(node),
  mimeType: node.mime_type ?? null,
  checksum: node.checksum ?? null,
  revision: node.storage_path ?? null,
  versions: node.versions ?? [],
  hasBytes: !!node.storage_path
});

/** A case file: saves go to `PUT /fs/.../node/:id/{bytes,pdf}`. */
export const caseFileStore = (
  individualId: string,
  node: FsNode
): EditableFileStore => ({
  file: caseFileOf(node),
  loadOriginal: () => {
    if (!node.storage_path) {
      return Promise.reject(new Error("This file has no stored bytes."));
    }
    return downloadFile(node.storage_path);
  },
  loadPdf: () => FilesystemAPI.getNodePdf(individualId, node.id),
  replaceBytes: async (blob, options) => {
    const { node: updated } = await FilesystemAPI.replaceNodeBytes(
      individualId,
      node.id,
      blob,
      { ...options, filename: nodeFilename(node) }
    );
    return caseFileOf(updated);
  },
  replacePdf: async (pdf, options) => {
    const { node: updated } = await FilesystemAPI.replaceNodePdf(
      individualId,
      node.id,
      pdf,
      options
    );
    return caseFileOf(updated);
  },
  restoreVersion: async (index) => {
    const { node: updated } = await FilesystemAPI.restoreVersion(
      individualId,
      node.id,
      index
    );
    return caseFileOf(updated);
  }
});
